import { Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Link } from '@liminal/web-ui/primitives/Link';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { formatOrderDate, resolveDeviceTimezone } from '@liminal/utils/date';
import type { ActiveWindow, RewardMerchant } from '@/api/types';
import { merchantAvailabilityPill, windowPhasePill, type WindowPhase } from '@/components/ui/statusHelpers';
import { isWindowOpen, mapsSearchUrl, merchantAvailability, nextWindowStart } from '@/lib/rewards';

// The partner detail page's catalogue (LIM-4607): why an active partner is
// hidden, and its links, locations and active windows as customers get them.

interface CatalogueDetailsProps {
  readonly merchant: RewardMerchant;
  /** Epoch milliseconds, from useNow, so every window is read against one moment. */
  readonly now: number;
}

/**
 * The line under the header for an active partner outside its windows:
 * customers cannot see it, and a payment made at it now does not qualify,
 * though one made during an earlier window can still be redeemed. Nothing
 * for any other partner.
 */
export function AvailabilityNotice({ merchant, now }: CatalogueDetailsProps) {
  const availability = merchantAvailability(merchant, now);
  if (availability !== 'scheduled' && availability !== 'between') return null;
  const next = nextWindowStart(merchant.activeWindows ?? [], now);
  const label = merchantAvailabilityPill(availability).label;
  const when = next
    ? ` until ${formatOrderDate(next)}`
    : availability === 'between'
      ? ', and no window is still to open'
      : ' until its first window opens';
  const earlier = availability === 'between' ? '; ones made during an earlier window can still be redeemed' : '';
  return (
    <WarningCard tone="info" compact data-testid="availability-notice">
      {`${label}: hidden in the app${when}. Payments made now do not qualify${earlier}.`}
    </WarningCard>
  );
}

function phaseOf(span: ActiveWindow, now: number): WindowPhase {
  if (isWindowOpen(span, now)) return 'open';
  return Date.parse(span.startTime) > now ? 'upcoming' : 'ended';
}

/** The partner's links, locations and active windows, each in its own card. */
export function CatalogueCards({ merchant, now }: CatalogueDetailsProps) {
  const links = merchant.links ?? [];
  const locations = merchant.locations ?? [];
  const windows = merchant.activeWindows ?? [];
  const zone = resolveDeviceTimezone();
  const rowSx = { py: 1, borderTop: `1px solid ${webColors.border.light}` };

  return (
    <>
      <Card variant="elevated">
        <Stack spacing={2}>
          <Typography variant="h6">Links</Typography>
          {links.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {links.map((link, i) => (
                <Button
                  key={`${i}:${link.uri}`}
                  variant="secondary"
                  size="sm"
                  endIcon="open-outline"
                  onClick={() => window.open(link.uri, '_blank', 'noopener,noreferrer')}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      <Card variant="elevated">
        <Stack spacing={2}>
          <Typography variant="h6">Locations</Typography>
          {locations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          ) : (
            <Stack>
              {locations.map((location, i) => {
                // Proto3 JSON: an absent coordinate is 0, a real one.
                const coordinates = { latitude: location.latitude ?? 0, longitude: location.longitude ?? 0 };
                return (
                  <Stack key={`${i}:${location.label}`} direction="row" spacing={2} alignItems="flex-start" sx={rowSx}>
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2">{location.label}</Typography>
                      {location.address && (
                        <Typography variant="body2" color="text.secondary">
                          {location.address}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {`${coordinates.latitude}, ${coordinates.longitude}`}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                      <Link href={mapsSearchUrl(coordinates)} external>
                        Open in Maps
                      </Link>
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Card>

      <Card variant="elevated">
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Active windows</Typography>
            <Typography variant="body2" color="text.secondary">
              {windows.length === 0
                ? 'None: live whenever it is active.'
                : `Payments made during a window can be redeemed after it closes. Shown in ${zone ? `your timezone, ${zone}` : "this browser's timezone"}.`}
            </Typography>
          </Stack>
          {windows.length > 0 && (
            <Stack>
              {windows.map((span, i) => {
                const phase = windowPhasePill(phaseOf(span, now));
                return (
                  <Stack
                    key={`${i}:${span.startTime}`}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={rowSx}
                  >
                    <Typography variant="body2">{`${formatOrderDate(span.startTime)} – ${formatOrderDate(span.endTime)}`}</Typography>
                    <StatusPill tone={phase.tone} label={phase.label} size="sm" />
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Card>
    </>
  );
}
